import type { ReactElement } from "react";
import {
  Activity,
  OctagonAlert,
  Clock,
  GitBranch,
  Target,
  TrendingDown,
  type LucideIcon,
} from "lucide-react";
import type {
  StrategicHealthIcon,
  StrategicHealthParams,
} from "@playbookai/widget-schemas";
import { cn } from "../../lib/utils.js";
import { statusToken } from "../../lib/types.js";
import type { WidgetBodyProps } from "../types.js";

type Stat = NonNullable<StrategicHealthParams["stats"]>[number];

// Bounded iconName → Lucide map (DB-01); falls back to a neutral mark.
const STAT_ICON: Record<StrategicHealthIcon, LucideIcon> = {
  target: Target,
  trendingDown: TrendingDown,
  alertOctagon: OctagonAlert,
  clock: Clock,
  gitBranch: GitBranch,
};

function StatCard({ stat }: { stat: Stat }): ReactElement {
  const token = statusToken(stat.status);
  // Member-access lookup (not a call) so the icon is a stable reference.
  const Icon = (stat.iconName && STAT_ICON[stat.iconName]) || Activity;
  return (
    <div className="flex flex-col bg-pb-card border border-pb-border rounded-lg p-4 pb-hairline shadow-sm">
      <div className="flex items-start justify-between gap-2 mb-3">
        <span className="min-w-0 text-sm font-medium text-pb-text-secondary leading-tight">
          {stat.label}
        </span>
        <Icon className="w-4 h-4 shrink-0 text-pb-text-muted" aria-hidden />
      </div>
      <span className="mt-auto flex items-end justify-between gap-2">
        <span className="text-3xl font-mono text-pb-text pb-tabular tracking-tight">
          {stat.value}
        </span>
        <span
          className={cn(
            "text-xs font-mono pb-tabular border border-pb-border rounded px-1.5 py-0.5",
            token.textClass,
          )}
        >
          {stat.trend}
        </span>
      </span>
    </div>
  );
}

/**
 * Strategic Health — dashboard data widget (Strategy, full-width). A row of
 * top-line transformation KPIs as stat cards, each with a per-stat icon and a
 * status-toned trend badge. Self-contained: `TView` = `TParams` — the `stats`
 * (incl. `iconName`) ride the saved params (seeded by the mock today).
 */
export function StrategicHealthWidget({
  data,
}: WidgetBodyProps<StrategicHealthParams>): ReactElement {
  const stats = data.stats ?? [];

  if (stats.length === 0) {
    return (
      <p className="text-dense-1 text-pb-text-muted italic">
        No strategic KPIs configured.
      </p>
    );
  }

  /* Five across from the widget's OWN width (PB-5436), not the window: the
     Player AI side rail — or a narrower span on the dashboard — leaves the
     card less room than the window suggests. 56.375rem is the card body at
     its default full span on a 1024px window, minus the 1rem scrollbar
     allowance. */
  return (
    <div className="@container/widget">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 @min-[56.375rem]/widget:grid-cols-5">
        {stats.map((stat) => (
          <StatCard key={stat.label} stat={stat} />
        ))}
      </div>
    </div>
  );
}
